import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Eye, MousePointer, Clock } from 'lucide-react';
import { MetricCard } from '@/components/admin/analytics/MetricCard';
import { VisitorsChart } from '@/components/admin/analytics/VisitorsChart';
import { TopPagesTable } from '@/components/admin/analytics/TopPagesTable';
import {
  useAnalyticsMetrics,
  useTopPages,
  useVisitorsByDay,
} from '@/hooks/useAnalytics';
import type { DateRange } from '@/lib/analytics/googleAnalyticsData';

export function AnalyticsDashboard() {
  const { t } = useTranslation('admin');
  const [dateRange, setDateRange] = useState<DateRange>('30daysAgo');

  // Récupération des données
  const { data: metrics, isLoading: metricsLoading } = useAnalyticsMetrics(dateRange);
  const { data: topPages, isLoading: topPagesLoading } = useTopPages(dateRange, 10);
  const { data: visitorsByDay, isLoading: visitorsLoading } = useVisitorsByDay(dateRange);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('nav.analytics')}</h1>
          <p className="text-gray-600 mt-2">Tableau de bord des statistiques du site</p>
        </div>

        {/* Sélecteur de période */}
        <Tabs value={dateRange} onValueChange={(value) => setDateRange(value as DateRange)}>
          <TabsList>
            <TabsTrigger value="7daysAgo">7 jours</TabsTrigger>
            <TabsTrigger value="30daysAgo">30 jours</TabsTrigger>
            <TabsTrigger value="90daysAgo">90 jours</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Cartes métriques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Visiteurs"
          value={metrics?.totalVisitors || 0}
          icon={Users}
          trend={metrics?.trends.visitors}
          format="number"
        />
        <MetricCard
          title="Pages vues"
          value={metrics?.totalPageViews || 0}
          icon={Eye}
          trend={metrics?.trends.pageViews}
          format="number"
        />
        <MetricCard
          title="Taux de rebond"
          value={metrics?.bounceRate || 0}
          icon={MousePointer}
          trend={metrics?.trends.bounceRate}
          format="percentage"
        />
        <MetricCard
          title="Durée moyenne"
          value={metrics?.avgSessionDuration || 0}
          icon={Clock}
          trend={metrics?.trends.sessionDuration}
          format="duration"
        />
      </div>

      {/* Graphique et table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique visiteurs */}
        <VisitorsChart data={visitorsByDay || []} isLoading={visitorsLoading} />

        {/* Table top pages */}
        <TopPagesTable data={topPages || []} isLoading={topPagesLoading} />
      </div>

      {/* Message de chargement général */}
      {(metricsLoading || topPagesLoading || visitorsLoading) && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <p className="text-sm text-gray-600">Chargement des données Analytics...</p>
        </div>
      )}
    </div>
  );
}
